import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEdit, FaStar, FaUpload } from 'react-icons/fa';
import axios from 'axios';
import './ServicesShowcase.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ServicesShowcase = ({ services = [], onChange, maxServices = Infinity, isPremium = false }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [presignedUrlCache, setPresignedUrlCache] = useState({});

  // Convert direct S3 URLs to presigned URLs for private bucket access
  const getPresignedUrlForImage = async (url) => {
    if (!url) return null;
    
    // If it's already a presigned URL (has query params), return as-is
    if (url.includes('?') && (url.includes('X-Amz-') || url.includes('AWSAccessKeyId'))) {
      return url;
    }
    
    // If it's not an S3 URL, return as-is
    if (!url.startsWith('http') || !url.includes('.s3.') || !url.includes('.amazonaws.com')) {
      return url;
    }
    
    // Check cache first
    if (presignedUrlCache[url]) {
      return presignedUrlCache[url];
    }
    
    // Convert direct S3 URL to presigned URL
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/upload/presigned`,
        { url },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const presignedUrl = response.data.presignedUrl;
      // Cache it
      setPresignedUrlCache(prev => ({ ...prev, [url]: presignedUrl }));
      return presignedUrl;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      return url; // Fallback to original URL
    }
  };

  const handleAdd = () => {
    if (services.length >= maxServices) {
      alert(`You can only add up to ${maxServices} services in your current plan.`);
      return;
    }
    setShowAddForm(true);
    setEditingIndex(null);
  };

  const handleSave = (serviceData) => {
    if (editingIndex !== null) {
      // Update existing service
      const updated = [...services];
      updated[editingIndex] = serviceData;
      onChange(updated);
      setEditingIndex(null);
    } else {
      // Add new service
      onChange([...services, serviceData]);
      setShowAddForm(false);
    }
  };

  const handleDelete = (index) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      const updated = services.filter((_, i) => i !== index);
      onChange(updated);
    }
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setShowAddForm(true);
  };

  const ServiceForm = ({ service = {}, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
      title: service.title || '',
      description: service.description || '',
      price: service.price || '',
      image: service.image || '',
      featured: service.featured || false,
    });
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState(service.image || '');
    
    // Update preview when service prop changes (for editing)
    React.useEffect(() => {
      if (service.image) {
        setImagePreview(service.image);
      }
    }, [service.image]);

    const handleImageUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate file type - only allow jpeg, jpg, png, webp
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const allowedExtensions = ['.jpeg', '.jpg', '.png', '.webp'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        alert('Please select a valid image file (JPEG, JPG, PNG, or WebP)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      setUploadingImage(true);
      try {
        const token = localStorage.getItem('token');
        const uploadFormData = new FormData();
        uploadFormData.append('serviceImage', file);

        const response = await axios.post(
          `${API_URL}/upload/service`,
          uploadFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        // imageUrl: Direct S3 URL or key (for database storage)
        // previewUrl: Presigned URL (for immediate preview in private buckets)
        const imageUrl = response.data.imageUrl;
        const previewUrl = response.data.previewUrl || imageUrl;
        
        // Store the direct URL/key in database (not the presigned URL, as it expires)
        setFormData({ ...formData, image: imageUrl });
        // Use presigned URL for immediate preview
        setImagePreview(previewUrl);
        
        // Verify image loads successfully
        const testImg = new Image();
        testImg.onload = () => {
          console.log('✅ Service image preview loaded successfully');
        };
        testImg.onerror = () => {
          console.warn('⚠️ Service image preview failed to load, but URL is saved');
          // Keep the preview anyway - it might work after reload
        };
        testImg.src = previewUrl;
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      } finally {
        setUploadingImage(false);
        e.target.value = ''; // Reset input
      }
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!formData.title.trim()) {
        alert('Service title is required');
        return;
      }
      onSave(formData);
      setFormData({ title: '', description: '', price: '', image: '', featured: false });
      setImagePreview('');
    };

    return (
      <div className="service-form">
        <h4>{editingIndex !== null ? 'Edit Service' : 'Add Service'}</h4>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>Price</label>
            <input
              type="text"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder="e.g., $99, Free, Contact for pricing"
            />
          </div>
          <div className="form-group">
            <label>Service Image</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label
                htmlFor={`service-image-upload-${editingIndex !== null ? editingIndex : 'new'}`}
                className="btn btn-secondary"
                style={{
                  cursor: uploadingImage ? 'not-allowed' : 'pointer',
                  opacity: uploadingImage ? 0.6 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  width: 'fit-content',
                }}
              >
                <FaUpload /> {uploadingImage ? 'Uploading...' : 'Upload Image (JPEG, JPG, PNG, WebP)'}
              </label>
              <input
                type="file"
                id={`service-image-upload-${editingIndex !== null ? editingIndex : 'new'}`}
                accept="image/jpeg,image/jpg,image/png,image/webp,.jpeg,.jpg,.png,.webp"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                style={{ display: 'none' }}
              />
              {(imagePreview || formData.image) && (
                <div style={{ marginTop: '0.5rem' }}>
                  <img
                    src={imagePreview || formData.image}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '4px',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {isPremium && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                />
                Featured Service
              </label>
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">Save</button>
            <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="services-showcase">
      <div className="services-header">
        <h3>Services Showcase</h3>
        {services.length < maxServices && (
          <button onClick={handleAdd} className="btn btn-primary btn-sm">
            <FaPlus /> Add Service
          </button>
        )}
        {services.length >= maxServices && maxServices !== Infinity && (
          <span className="limit-reached">
            Limit reached ({maxServices} services max)
          </span>
        )}
      </div>

      {showAddForm && (
        <ServiceForm
          service={editingIndex !== null ? services[editingIndex] : {}}
          onSave={handleSave}
          onCancel={() => {
            setShowAddForm(false);
            setEditingIndex(null);
          }}
        />
      )}

      {services.length === 0 && !showAddForm && (
        <div className="empty-state">
          <p>No services added yet. Click "Add Service" to get started.</p>
        </div>
      )}

      <div className="services-list">
        {services.map((service, index) => {
          const ServiceImage = ({ imageUrl, alt }) => {
            const [displayUrl, setDisplayUrl] = useState(null);
            const [loading, setLoading] = useState(true);

            useEffect(() => {
              if (!imageUrl) {
                setLoading(false);
                return;
              }

              // Format image URL for display (handle both relative and absolute URLs)
              const formatUrl = (url) => {
                if (!url) return null;
                if (url.startsWith('http')) return url;
                const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                return `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;
              };

              const formattedUrl = formatUrl(imageUrl);
              
              // If it's a direct S3 URL, convert to presigned URL
              if (formattedUrl && formattedUrl.includes('.s3.') && formattedUrl.includes('.amazonaws.com') && !formattedUrl.includes('?')) {
                getPresignedUrlForImage(formattedUrl).then(url => {
                  setDisplayUrl(url || formattedUrl);
                  setLoading(false);
                });
              } else {
                setDisplayUrl(formattedUrl);
                setLoading(false);
              }
            }, [imageUrl]);

            if (!imageUrl || loading) {
              return loading ? (
                <div className="service-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', minHeight: '150px' }}>
                  <span>Loading...</span>
                </div>
              ) : null;
            }

            return (
              <div className="service-image">
                <img 
                  src={displayUrl} 
                  alt={alt}
                  onError={(e) => {
                    // Fallback: try the original URL if formatted URL fails
                    if (e.target.src !== imageUrl) {
                      e.target.src = imageUrl;
                    }
                  }}
                />
              </div>
            );
          };

          return (
            <div key={index} className={`service-item ${service.featured ? 'featured' : ''}`}>
              {service.image && (
                <ServiceImage imageUrl={service.image} alt={service.title} />
              )}
            <div className="service-content">
              <div className="service-header">
                <h4>
                  {service.title}
                  {service.featured && <FaStar className="featured-icon" />}
                </h4>
                <div className="service-actions">
                  <button onClick={() => handleEdit(index)} className="btn-icon">
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDelete(index)} className="btn-icon btn-danger">
                    <FaTrash />
                  </button>
                </div>
              </div>
              {service.description && <p className="service-description">{service.description}</p>}
              {service.price && <p className="service-price">{service.price}</p>}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServicesShowcase;

